import { useEffect, useState, type FormEvent } from 'react';
import { Package, Plus, Trash2 } from 'lucide-react';
import { api } from '../../api';
import { Modal } from '../../componentes/Modal';

export interface Peca {
  id: string; produto_id: string; sku: string | null; nome: string; unidade: string; origem: string; quantidade: string; acao: string;
  ativo_id: string | null; ativo: string | null; veiculo_placa: string | null; lancado_em: string | null; criado_em: string; usuario: string | null;
  baixa: 'pendente' | 'entregue' | 'falhou' | 'descartado' | null; baixa_erro: string | null;
}
interface Produto { id: string; sku: string | null; nome: string; unidade: string; origem: string }
interface Veiculo { id: string; placa: string; descricao: string | null; ativo: boolean }

const NOME_ACAO: Record<string, string> = { aplicado: 'Aplicada no cliente', devolvido: 'Sobrou (volta ao estoque)', retirado: 'Retirada do cliente (defeito)' };
const NOME_BAIXA: Record<string, [string, string]> = {
  pendente: ['Na fila do estoque', 's-na'], entregue: ['Baixada no estoque', 's-ok'], falhou: ['Falha na baixa', 's-nok'], descartado: ['Fora do estoque', 's-na'],
};
const qtd = (q: string, un: string) => `${Number(q).toLocaleString('pt-BR', { maximumFractionDigits: 3 })} ${un}`;
const placaFmt = (p: string) => `${p.slice(0, 3)}-${p.slice(3)}`;
const uuid7 = () => {
  const h = Date.now().toString(16).padStart(12, '0');
  const r = [...crypto.getRandomValues(new Uint8Array(10))].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8)}-7${r.slice(0, 3)}-${((parseInt(r[3]!, 16) & 3) | 8).toString(16)}${r.slice(4, 7)}-${r.slice(7, 19)}`;
};

function FormPeca({ osId, ativos, aoSalvar, aoCancelar }: { osId: string; ativos: { id: string; apelido: string }[]; aoSalvar: () => void; aoCancelar: () => void }) {
  const [busca, setBusca] = useState('');
  const [opcoes, setOpcoes] = useState<Produto[]>([]);
  const [produto, setProduto] = useState<Produto | null>(null);
  const [quantidade, setQuantidade] = useState('1');
  const [acao, setAcao] = useState('aplicado');
  const [ativo, setAtivo] = useState('');
  const [veiculo, setVeiculo] = useState('');
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  // id gerado uma vez: clicar "Lançar" duas vezes não lança duas peças
  const [id] = useState(uuid7);

  useEffect(() => { api<Veiculo[]>('/veiculos').then((v) => setVeiculos(v.filter((x) => x.ativo))).catch(() => setVeiculos([])); }, []);
  useEffect(() => {
    if (produto) return;
    const t = setTimeout(() => {
      api<Produto[]>(`/produtos?busca=${encodeURIComponent(busca.trim())}&limite=15`).then(setOpcoes).catch(() => setOpcoes([]));
    }, 250);
    return () => clearTimeout(t);
  }, [busca, produto]);

  const q = Number(quantidade.replace(',', '.'));
  const qOk = Number.isFinite(q) && q > 0 && Math.round(q * 1000) === q * 1000;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (!produto || !qOk) return;
    setErro(null); setSalvando(true);
    try {
      await api(`/os/${osId}/pecas`, { metodo: 'POST', corpo: { id, produto_id: produto.id, quantidade: q, acao, veiculo_id: veiculo || null, ativo_id: ativo || null } });
      aoSalvar();
    } catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível lançar.'); } finally { setSalvando(false); }
  }

  return (
    <form className="form" onSubmit={enviar}>
      {!produto ? (
        <label>Peça
          <input type="search" autoFocus value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nome ou código (ex.: capacitor, CAP-25)" />
          <ul className="lista-escolha" role="listbox">
            {opcoes.map((p) => (
              <li key={p.id}><button type="button" onClick={() => setProduto(p)}><strong>{p.nome}</strong><small>{p.sku ?? 'sem código'} · {p.unidade}{p.origem === 'local' ? ' · fora do EstoqueMaza' : ''}</small></button></li>
            ))}
            {opcoes.length === 0 && <li className="nota">Nenhuma peça ativa com esse nome ou código.</li>}
          </ul>
        </label>
      ) : (
        <div className="escolhido">
          <div><strong>{produto.nome}</strong><small>{produto.sku ?? 'sem código'} · {produto.unidade}</small></div>
          <button type="button" className="btn-link" onClick={() => setProduto(null)}>trocar</button>
        </div>
      )}
      <div className="linha-2">
        <label>Quantidade{produto ? ` (${produto.unidade})` : ''}
          <input inputMode="decimal" value={quantidade} onChange={(e) => setQuantidade(e.target.value)} aria-invalid={!qOk} />
          {!qOk && <small className="ajuda erro">Maior que zero, até 3 casas (ex.: 12,5).</small>}
        </label>
        <label>O que aconteceu
          <select value={acao} onChange={(e) => setAcao(e.target.value)}>{Object.entries(NOME_ACAO).map(([v, r]) => <option key={v} value={v}>{r}</option>)}</select>
        </label>
      </div>
      <div className="linha-2">
        <label>Saiu de (carro)
          <select value={veiculo} onChange={(e) => setVeiculo(e.target.value)}>
            <option value="">Carro do técnico ou almoxarifado</option>
            {veiculos.map((v) => <option key={v.id} value={v.id}>{placaFmt(v.placa)}{v.descricao ? ` — ${v.descricao}` : ''}</option>)}
          </select>
        </label>
        {ativos.length > 0 && (
          <label>Equipamento (opcional)
            <select value={ativo} onChange={(e) => setAtivo(e.target.value)}>
              <option value="">—</option>
              {ativos.map((a) => <option key={a.id} value={a.id}>{a.apelido}</option>)}
            </select>
          </label>
        )}
      </div>
      <small className="ajuda">A peça lançada não se edita: para corrigir, exclua e lance de novo. A baixa vai para o EstoqueMaza sozinha.</small>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      <div className="acoes-form">
        <button type="button" className="btn-fantasma" onClick={aoCancelar}>Cancelar</button>
        <button className="btn" disabled={salvando || !produto || !qOk}>{salvando ? 'Lançando…' : 'Lançar peça'}</button>
      </div>
    </form>
  );
}

/** Peças da OS: lista com a situação da baixa no estoque; lançar/excluir com a OS aberta. */
export function Pecas({ osId, pecas, ativos, podeEditar, aoMudar }: { osId: string; pecas: Peca[]; ativos: { id: string; apelido: string }[]; podeEditar: boolean; aoMudar: () => void }) {
  const [novo, setNovo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  if (!podeEditar && pecas.length === 0) return null;

  async function excluir(p: Peca) {
    if (!window.confirm(`Excluir ${qtd(p.quantidade, p.unidade)} de ${p.nome}?${p.baixa === 'entregue' ? ' A baixa já foi feita: o estoque recebe um estorno.' : ''}`)) return;
    setErro(null);
    try { await api(`/os/${osId}/pecas/${p.id}/excluir`, { metodo: 'POST' }); aoMudar(); } catch (e) { setErro((e as Error).message); }
  }

  return (
    <section className="secao">
      <div className="secao-topo">
        <h2>Peças <span className="contador">{pecas.length}</span></h2>
        {podeEditar && <button className="btn-fantasma" onClick={() => setNovo(true)}><Plus size={16} aria-hidden /> Peça</button>}
      </div>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      {pecas.length === 0 ? <p className="nota">Nenhuma peça lançada.</p> : (
        <div className="cartao tabela-cartao rolagem-x">
          <table className="tabela">
            <thead><tr><th>Peça</th><th className="num">Qtd.</th><th>Movimento</th><th>Carro</th><th>Estoque</th>{podeEditar && <th aria-label="Ações" />}</tr></thead>
            <tbody>
              {pecas.map((p) => {
                const [rot, cls] = p.baixa ? NOME_BAIXA[p.baixa] ?? [p.baixa, 's-na'] : ['—', 's-na'];
                return (
                  <tr key={p.id}>
                    <td><Package size={14} aria-hidden /> <strong>{p.nome}</strong><small>{p.sku ?? 'sem código'}{p.ativo ? ` · ${p.ativo}` : ''}{p.usuario ? ` · ${p.usuario}` : ''}</small></td>
                    <td className="num">{qtd(p.quantidade, p.unidade)}</td>
                    <td>{NOME_ACAO[p.acao] ?? p.acao}</td>
                    <td className="mono">{p.veiculo_placa ? placaFmt(p.veiculo_placa) : '—'}</td>
                    <td><span className={`situacao ${cls}`}>{rot}</span>{p.baixa === 'falhou' && p.baixa_erro && <small>{p.baixa_erro}</small>}</td>
                    {podeEditar && <td className="acoes"><button className="btn-icone" onClick={() => excluir(p)} aria-label={`Excluir ${p.nome}`}><Trash2 size={16} /></button></td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Modal titulo="Lançar peça" aberto={novo} aoFechar={() => setNovo(false)}>
        <FormPeca osId={osId} ativos={ativos} aoCancelar={() => setNovo(false)} aoSalvar={() => { setNovo(false); aoMudar(); }} />
      </Modal>
    </section>
  );
}
